import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
// The build style's fonts (VITE_UI_STYLE, default Dusk), in the entry CSS before the first
// paint: vite.config.ts aliases this to src/styles/fonts/<style>.css (src/config/uiStyleBuild.ts).
import 'virtual:ui-style-fonts.css';
import './styles/globals.css';
import './i18n';
import '@/utils/soundPreference';
import { installPreloadErrorRecovery } from '@/utils/preloadErrorRecovery';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { DeviceModeProvider, bootDeviceMode } from './contexts/DeviceModeContext';
import { SurfaceProvider, readInitialSurface } from './contexts/SurfaceContext';
import { surfaceEnvironment } from './config/surfaceEnv';
import { UiStyleProvider, bootUiStyle } from './contexts/UiStyleContext';
import { hasNativeHost } from './platform/kioskHost';
import { syncServiceWorker } from './platform/serviceWorker';

/**
 * Avoid a style flash: <html data-ui-style> (VITE_UI_STYLE, or a preview ?style=) is set before the
 * first render; a previewed style's fonts (src/styles/styleFonts.ts) start loading.
 */
bootUiStyle();

/**
 * Avoid a layout/size flash before DeviceModeProvider mounts: data-mode, the device class and
 * --k-scale (from the initial surface: ?surface=, the cached kiosk info, VITE_DOMAIN_TYPE) are set
 * before the first render.
 */
const surfaceEnv = surfaceEnvironment();
bootDeviceMode(readInitialSurface(surfaceEnv));

// A chunk that fails to load: a stylesheet (e.g. a previewed style's fonts) is skipped, keeping
// the fallback; a stale JS chunk after a deploy reloads once per build and session (no loop).
installPreloadErrorRecovery(window, {
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
  buildId: import.meta.url,
});

// The PWA's service worker: registered as vite-plugin-pwa's script did, but never inside the native
// kiosk shell (src/platform/serviceWorker.ts).
syncServiceWorker({
  pwaEnabled: import.meta.env.VITE_DISABLE_PWA !== 'true',
  hostPresent: hasNativeHost(),
  container: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
  whenLoaded: (fn) => {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', fn, { once: true });
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <TimezoneProvider>
      {/* Kiosk / non-kiosk (src/config/surfacePolicy.ts); DeviceModeProvider reads it for the kiosk class. */}
      <SurfaceProvider environment={surfaceEnv}>
        <DeviceModeProvider>
          <UiStyleProvider>
            <App />
          </UiStyleProvider>
        </DeviceModeProvider>
      </SurfaceProvider>
    </TimezoneProvider>
  </StrictMode>
);
